import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  UsageFeatures,
  UsageItemDetail,
  UsageItemType,
  UsagePageInteraction,
  UsagePageStat,
  UsageSession,
  UsageSummary,
  UsageTrendPoint,
  UsageUser,
  UsageWalkthroughEvent
} from '../models/usage-report';

/**
 * Usage-analytics reporting endpoints — SuperAdmin-only on
 * the API. `env` empty means "let the API default to its own environment".
 */
@Injectable({
  providedIn: 'root'
})
export class UsageReportService {
  baseUrl = environment.apiEndpoint + '/usagereport/';

  constructor(private http: HttpClient) { }

  GetSummary(startDate: Date, endDate: Date, env: string): Observable<UsageSummary> {
    return this.http.get<UsageSummary>(this.baseUrl + 'summary', { params: this.rangeParams(startDate, endDate, env) });
  }

  GetTrends(startDate: Date, endDate: Date, env: string): Observable<UsageTrendPoint[]> {
    return this.http.get<UsageTrendPoint[]>(this.baseUrl + 'trends', { params: this.rangeParams(startDate, endDate, env) });
  }

  GetUsers(startDate: Date, endDate: Date, env: string): Observable<UsageUser[]> {
    return this.http.get<UsageUser[]>(this.baseUrl + 'users', { params: this.rangeParams(startDate, endDate, env) });
  }

  GetSessions(startDate: Date, endDate: Date, env: string, userId?: string): Observable<UsageSession[]> {
    let params = this.rangeParams(startDate, endDate, env);
    if (userId) params = params.set('userId', userId);
    return this.http.get<UsageSession[]>(this.baseUrl + 'sessions', { params });
  }

  GetWalkthrough(sessionId: string, env: string): Observable<UsageWalkthroughEvent[]> {
    let params = new HttpParams();
    if (env) params = params.set('environment', env);
    return this.http.get<UsageWalkthroughEvent[]>(this.baseUrl + 'sessions/' + sessionId + '/walkthrough', { params });
  }

  GetPages(startDate: Date, endDate: Date, env: string): Observable<UsagePageStat[]> {
    return this.http.get<UsagePageStat[]>(this.baseUrl + 'pages', { params: this.rangeParams(startDate, endDate, env) });
  }

  GetFeatures(startDate: Date, endDate: Date, env: string): Observable<UsageFeatures> {
    return this.http.get<UsageFeatures>(this.baseUrl + 'features', { params: this.rangeParams(startDate, endDate, env) });
  }

  GetPageInteractions(routePattern: string, startDate: Date, endDate: Date, env: string): Observable<UsagePageInteraction[]> {
    const params = this.rangeParams(startDate, endDate, env).set('routePattern', routePattern);
    return this.http.get<UsagePageInteraction[]>(this.baseUrl + 'pageinteractions', { params });
  }

  GetItemDetail(type: UsageItemType, name: string, startDate: Date, endDate: Date, env: string): Observable<UsageItemDetail> {
    const params = this.rangeParams(startDate, endDate, env)
      .set('type', type)
      .set('name', name);
    return this.http.get<UsageItemDetail>(this.baseUrl + 'item', { params });
  }

  private rangeParams(startDate: Date, endDate: Date, env: string): HttpParams {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());
    if (env) params = params.set('environment', env);
    return params;
  }
}
